/**
 * Converting a picture into a grid of cells, to build a level that looks like
 * the image.
 *
 * An image is a grid of pixels, each with red, green, blue and alpha
 * (transparency) values from 0 to 255. Each pixel becomes one cell:
 * - a transparent pixel becomes an empty cell;
 * - a very dark pixel also becomes empty. Pixel-art sprites usually have dark
 *   outlines, and the game has no dark colors (they'd fail the contrast
 *   checks), so outlines would otherwise turn into a random-looking color;
 * - every other pixel becomes whichever game color looks closest.
 */
import { MAX_GRID_SIZE } from "./level";
import { luminance, PALETTE } from "./palette";
import type { Cell, Color } from "./state";

/** Settings for turning an image into a grid. */
export interface ImageGridOptions {
	/** Pixels less opaque than this (alpha 0 to 255) become empty cells. */
	alphaCutoff: number;
	/**
	 * Pixels darker than this become empty cells. Brightness is measured as
	 * "relative luminance" from 0 (black) to 1 (white), the same measure used for
	 * the accessibility contrast checks.
	 */
	darkCutoff: number;
	/**
	 * The largest grid size allowed. Bigger images are shrunk to fit by keeping
	 * evenly spaced pixels ("nearest neighbour" scaling), which keeps pixel art
	 * crisp.
	 */
	maxSize: number;
	/** How many palette colors to use (the first N). */
	colors: number;
}

/** Settings that suit small pixel-art sprites with dark outlines. */
export const DEFAULT_IMAGE_OPTIONS: ImageGridOptions = {
	alphaCutoff: 128,
	darkCutoff: 0.04,
	// Up to the game's limit, so sample pictures (up to 64x64) keep every pixel.
	maxSize: MAX_GRID_SIZE,
	colors: PALETTE.length,
};

/**
 * A grid made from an image, ready to pass to
 * `generateLevelWithSolution(options, cells)` in level.ts.
 */
export interface ImageGrid {
	width: number;
	height: number;
	/** One entry per cell, row after row; `null` for empty. */
	cells: Cell[];
}

/**
 * Image pixels as the browser's canvas returns them (`ImageData`): one flat
 * list of numbers, four per pixel (red, green, blue, alpha), row after row.
 * Defined as a plain shape, not the browser type, so tests can build their own.
 */
export interface Pixels {
	width: number;
	height: number;
	data: ArrayLike<number>;
}

/**
 * Convert an image to a grid: one cell per pixel (after shrinking, if
 * needed), each either the closest game color or empty.
 */
export function imageToGrid(
	image: Pixels,
	options: Partial<ImageGridOptions> = {},
): ImageGrid {
	const { alphaCutoff, darkCutoff, maxSize, colors } = {
		...DEFAULT_IMAGE_OPTIONS,
		...options,
	};
	const scale = Math.max(1, image.width / maxSize, image.height / maxSize);
	const width = Math.max(1, Math.round(image.width / scale));
	const height = Math.max(1, Math.round(image.height / scale));
	const palette = PALETTE.slice(0, Math.max(1, colors)).map((s) => rgb(s.fill));

	const cells: Cell[] = [];
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			// Read the source pixel at the middle of the area this cell covers.
			const sx = Math.min(image.width - 1, Math.floor((x + 0.5) * scale));
			const sy = Math.min(image.height - 1, Math.floor((y + 0.5) * scale));
			const i = (sy * image.width + sx) * 4;
			const pixel: RGB = [
				image.data[i] ?? 0,
				image.data[i + 1] ?? 0,
				image.data[i + 2] ?? 0,
			];
			const alpha = image.data[i + 3] ?? 255;
			cells.push(
				alpha < alphaCutoff || luminance(hex(pixel)) < darkCutoff
					? null
					: nearest(pixel, palette),
			);
		}
	}
	return { width, height, cells };
}

type RGB = [number, number, number];

/** Turn a color written as "#rrggbb" into its red, green and blue numbers. */
function rgb(hexColor: string): RGB {
	return [1, 3, 5].map((i) =>
		Number.parseInt(hexColor.slice(i, i + 2), 16),
	) as RGB;
}

/** Turn red, green and blue numbers back into "#rrggbb". */
function hex([r, g, b]: RGB): string {
	return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Find the palette color closest to a pixel's color.
 *
 * Treating colors as points in 3D space (red, green, blue) and measuring the
 * distance between them is the simple approach, but human eyes don't see
 * those three channels equally. The "redmean" formula weights the channels
 * to better match what people perceive, and is still cheap to compute.
 */
export function nearest(pixel: RGB, palette: RGB[]): Color {
	let best = 0;
	let bestDistance = Number.POSITIVE_INFINITY;
	palette.forEach((p, index) => {
		const rm = (pixel[0] + p[0]) / 2;
		const dr = pixel[0] - p[0];
		const dg = pixel[1] - p[1];
		const db = pixel[2] - p[2];
		const distance =
			(2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db;
		if (distance < bestDistance) {
			bestDistance = distance;
			best = index;
		}
	});
	return best;
}
